import { useState } from 'react'

import {
  Box,
  Icon,
  IconBadge,
  Tag,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { CustomFieldsFieldFragment } from 'generated/resources/graphql'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { InjectHoverProvider, useHover } from 'modules/core/lib/hoverContext'
import i18n from 'modules/core/lib/i18n'
import { ModalStates } from 'modules/core/lib/questionModalStates'

import {
  getFieldsModalAction,
  getFieldsModalDescription,
  getFieldsModalTitle,
  getListIcon,
  getModalIlustration,
} from 'modules/contracts_settings/components/ContractFields/Taxonomies/taxonomyTranslator'
import Options from 'modules/custom_resources/components/CustomFields/Item/Options'
import useCustomFieldHandler from 'modules/legal_entities/hooks/useCustomFieldHandler'
import useCustomFieldMutations from 'modules/legal_entities/hooks/useCustomFieldMutations'
import FieldModal from 'modules/legal_entities/pages/settings/ContractsLegalEntityPage/ContractFields/CustomField/FieldModal'
import { CustomFieldsField } from 'modules/legal_entities/pages/settings/ContractsLegalEntityPage/ContractFields/types'

type Props = {
  customField: CustomFieldsFieldFragment
  canCreateCustomFields: boolean
  companyId: number
  legalEntityId: number
}
const formatCustomField = (field: CustomFieldsFieldFragment): CustomFieldsField => {
  return {
    ...field,
    required: field.required ?? false,
    options: field.options.map((option) => ({
      ...option,
      isActive: option.isActive ?? false,
    })),
  }
}

function DragAndDrop() {
  const isHover = useHover()

  if (!isHover) return null

  return <Icon.Medium icon="drag-and-drop" />
}

const CustomField = ({ customField, canCreateCustomFields, companyId, legalEntityId }: Props) => {
  const [modalType, setModalType] = useState<ModalStates>('none')
  const [modalSubmitAction, setModalSubmitAction] = useState<(field?: CustomFieldsField) => void>(
    () => {}
  )
  const [open, toggle] = useToggle()
  const {
    field,
    setField,
    handleInputChange,
    handleMandatoryChange,
    handleTypeChange,
    handleAddOption,
    handleOptionAction,
  } = useCustomFieldHandler(formatCustomField(customField))

  const {
    handleCreateCustomField,
    handleDelete,
    handleUpdateCustomField,
    loadingDeleteCustomField,
    loadingCreateCustomField,
    loadingUpdateCustomField,
  } = useCustomFieldMutations({
    companyId,
    legalEntityId,
    field,
    setModalType,
  })

  useUnsafeEffect(() => {
    if (customField) {
      setField(formatCustomField(customField))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customField])

  const actionsOverField = (() => {
    const actions = []
    const editActions = {
      onClick: () => {
        setModalType('edit')
        setModalSubmitAction(() => handleUpdateCustomField)
      },
      title: i18n.t('custom_fields.actions.edit'),
    }

    const duplicateActions = {
      onClick: () => {
        setModalType('duplicate')
        setModalSubmitAction(() => handleCreateCustomField)
      },
      title: i18n.t('custom_fields.actions.duplicate_question'),
    }

    const deleteActions = {
      onClick: () => {
        setModalType('delete')
        setModalSubmitAction(() => handleDelete)
      },
      type: 'wrong',
      title: i18n.t('custom_fields.actions.delete.remove_question'),
    }

    if (canCreateCustomFields) {
      actions.push(duplicateActions)
      actions.push(editActions)
      actions.push(deleteActions)
    }

    return actions
  })()

  return (
    <>
      <InjectHoverProvider>
        <Box
          flexDirection="row"
          alignItems="center"
          width="full"
          justifyContent="spaceBetween"
          paddingX="s4"
          paddingY="s16"
        >
          <Box flexDirection="row" gap="s16" alignItems="center">
            <Box flexDirection="row" gap="s2">
              <Box width="s24" flexNoShrink>
                <DragAndDrop />
              </Box>
              <IconBadge
                size="s24"
                icon={getListIcon(field?.fieldType || '')}
                iconColor="viridian1200"
                color="viridian200"
              />
            </Box>
            <Text.Normal size="300" weight="medium" color="grey1000">
              {customField.labelText}
            </Text.Normal>
          </Box>
          <Box gap="s16" flexDirection="row" alignItems="center" justifyContent="flexEnd">
            {!!customField.required && (
              <Tooltip.Alt
                attachment="bottom center"
                open={open}
                toggle={toggle}
                label={i18n.t(
                  'settings.legal_entities.legal_entity.contracts.contract_fields.mandatory_tooltip'
                )}
              >
                <Tag type="grey" size="normal" iconColor="grey800">
                  {i18n.t('ats.custom_fields.label.required')}
                </Tag>
              </Tooltip.Alt>
            )}
            <Options actions={actionsOverField} />
          </Box>
        </Box>
      </InjectHoverProvider>
      {modalType !== 'none' && (
        <FieldModal
          modalTitle={getFieldsModalTitle(modalType)}
          modalDescription={getFieldsModalDescription(modalType, field?.labelText)}
          modalIllustration={getModalIlustration(modalType)}
          modalButton={getFieldsModalAction(modalType)}
          onSubmit={() => modalSubmitAction(field)}
          onClose={() => setModalType('none')}
          onInputChange={(value) => handleInputChange(value)}
          onMandatoryChange={(value) => handleMandatoryChange(value)}
          onTypeChange={(value) => handleTypeChange(value)}
          onAddOption={handleAddOption}
          onOptionAction={handleOptionAction}
          field={field}
          duplicate={modalType === 'duplicate'}
          canSubmit={!!field?.fieldType}
          isSubmitting={
            loadingDeleteCustomField || loadingCreateCustomField || loadingUpdateCustomField
          }
          actionType={modalType}
        />
      )}
    </>
  )
}
export default CustomField
